import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  fetchTreatment,
  updateTreatment,
  createTreatment,
  fetchProcedures,
  addProcedure,
  updateProcedure,
  fetchTreatmentPrescriptions,
  clearTreatment,
} from '../store/treatmentsSlice'
import type { TreatmentDetail as TreatmentDetailType } from '../store/treatmentsSlice'
import { fetchVeterinarians } from '../store/settingsSlice'
import { fetchMedications } from '../store/medicationListSlice'
import { TreatmentDetailHeader } from '../components/treatments/TreatmentDetailHeader'
import { DiagnosisNotes } from '../components/treatments/DiagnosisNotes'
import { Procedures } from '../components/treatments/Procedures'
import { PrescribedMedications } from '../components/treatments/PrescribedMedications'
import { FollowUp } from '../components/treatments/FollowUp'
import { NewPrescriptionModal } from '../components/treatments/NewPrescriptionModal'

export function TreatmentDetailPage() {
  const { treatmentId } = useParams<{ treatmentId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { treatment, procedures, prescriptions, loading } = useAppSelector((s) => s.treatments)
  const veterinarians = useAppSelector((s) => s.settings.veterinarians)
  const medications = useAppSelector((s) => s.medicationList.medications)

  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false)

  useEffect(() => {
    if (treatmentId) {
      dispatch(fetchTreatment(treatmentId))
      dispatch(fetchProcedures(treatmentId))
      dispatch(fetchTreatmentPrescriptions(treatmentId))
      dispatch(fetchVeterinarians())
      dispatch(fetchMedications())
    }
    return () => {
      dispatch(clearTreatment())
    }
  }, [dispatch, treatmentId])

  const handleUpdate = async (data: Partial<TreatmentDetailType>) => {
    if (!treatmentId) return
    await dispatch(updateTreatment({ id: treatmentId, data }))
  }

  const handleCancel = async () => {
    if (!treatmentId) return
    await dispatch(updateTreatment({ id: treatmentId, data: { status: 'Cancelled' } }))
  }

  const handleAddProcedure = async (data: { treatment_id: string; name: string; description: string }) => {
    await dispatch(addProcedure(data))
  }

  const handleToggleProcedureStatus = async (id: string, newStatus: string) => {
    await dispatch(updateProcedure({ id, data: { status: newStatus } }))
  }

  const handleCreatePrescription = async (data: {
    patient_id: string
    medication_id: string
    treatment_id: string
    veterinarian_id: string
    dosage: string
    frequency: string
    route: string
    duration: string
    quantity_dispensed: number
    total_refills: number
    start_date: string
  }) => {
    const res = await fetch('/.netlify/functions/prescriptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (res.ok && treatmentId) {
      dispatch(fetchTreatmentPrescriptions(treatmentId))
    }
  }

  const handleCreateFollowUp = async () => {
    if (!treatment) return
    const result = await dispatch(createTreatment({
      patient_id: treatment.patient_id,
      veterinarian_id: treatment.veterinarian_id,
      treatment_type: 'Follow-Up',
      treatment_date: treatment.follow_up_date || new Date().toISOString().slice(0, 10),
      follow_up_notes: treatment.follow_up_notes || '',
      parent_treatment_id: treatment.id,
      status: 'Planned',
    }))
    if (createTreatment.fulfilled.match(result)) {
      navigate(`/treatments/${result.payload.id}`)
    }
  }

  if (loading || !treatment) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="treatment-detail-page">
        <p className="text-[13px] text-text-muted">Loading treatment...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="treatment-detail-page">
      <TreatmentDetailHeader
        treatment={treatment}
        veterinarians={veterinarians}
        onUpdate={handleUpdate}
        onCancel={handleCancel}
      />

      <div className="flex flex-col gap-4 mt-4">
        <DiagnosisNotes treatment={treatment} onUpdate={handleUpdate} />
        <Procedures
          procedures={procedures}
          treatmentId={treatment.id}
          onAdd={handleAddProcedure}
          onToggleStatus={handleToggleProcedureStatus}
        />
        <PrescribedMedications
          prescriptions={prescriptions}
          onCreatePrescription={() => setPrescriptionModalOpen(true)}
        />
        <FollowUp
          treatment={treatment}
          onUpdate={handleUpdate}
          onCreateFollowUp={handleCreateFollowUp}
        />
      </div>

      {prescriptionModalOpen && (
        <NewPrescriptionModal
          treatment={treatment}
          medications={medications}
          veterinarians={veterinarians}
          onSubmit={handleCreatePrescription}
          onClose={() => setPrescriptionModalOpen(false)}
        />
      )}
    </div>
  )
}
